/**
 * AllergyDetailSheet — Plan 04-03 / UI-SPEC §"<AllergyDetailSheet />".
 *
 * Bottom sheet titled "Allergier i husholdningen" listing per-member
 * allergy + dietary data with PixelAvatar rows. Reached by tapping the
 * <AllergyChip> at the top of <ShoppingRoute>.
 *
 * D-06 + D-14: per-member breakdown for the household. T-04-PII
 * mitigation: shown only inside the household — GET /api/members/dietary
 * requires login and is scoped to the session's family.
 *
 * #148: the sheet used to read /api/members/basic, which has never returned
 * dietary data, so it always rendered empty. /members/dietary returns short
 * name, colour and the allergy/diet chips only (MemberDietaryRow).
 *
 * #157 (redesign steg 7d, D9): arket er fortsatt et ark. Radene har
 * InitialAvatar (D2) i en tallboks, med navnet først i fet skrift; innholdet
 * er det samme som før.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';
import { LoadingState } from '../../components/primitives/LoadingState';
import { ErrorState } from '../../components/primitives/ErrorState';
import { dietaryLabels, useHouseholdDietary } from './useHouseholdDietary';

interface AllergyDetailSheetProps {
  open: boolean;
  onClose: () => void;
}

export function AllergyDetailSheet({ open, onClose }: AllergyDetailSheetProps) {
  const { t } = useTranslation();
  const { data: members, isPending, isError, refetch } = useHouseholdDietary();

  // #148: hent på nytt når arket åpnes — en admins endring skal synes med en
  // gang. Spørringen deles med brikken, som allerede er montert.
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  const rows = (members ?? [])
    .map((m) => {
      const labels = dietaryLabels(m, t);
      return { m, list: [...labels.allergies, ...labels.dietary].join(', ') };
    })
    // Bare medlemmer med minst én allergi eller kostholdsoppføring.
    .filter((r) => r.list.length > 0);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('shopping.allergy_sheet.title')}
    >
      <div data-testid="shopping-allergy-sheet">
        {/* #148: lasting og feil får egne tilstander. En tom liste ser ut som
            «ingen allergier», og det er farlig når noen har en alvorlig. */}
        {isError ? (
          <div data-testid="shopping-allergy-sheet-error">
            <ErrorState
              variant="inline"
              heading={t('shopping.allergy_sheet.error.heading')}
              body={t('shopping.allergy_sheet.error.body')}
              onRetry={() => void refetch()}
            />
          </div>
        ) : isPending ? (
          <div data-testid="shopping-allergy-sheet-loading">
            <LoadingState lines={3} />
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map(({ m, list }) => (
              <li
                key={m.id}
                data-testid={`shopping-allergy-row-${m.id}`}
                className="flex items-center gap-3 rounded-row bg-inset px-3 py-2.5"
              >
                <InitialAvatar name={m.short_name} colorSlot={asColorSlot(m.color_token)} decorative />
                <span className="min-w-0 flex-1 text-body text-ink">
                  {t('shopping.allergy_sheet.row', { name: m.short_name, list })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </BottomSheet>
  );
}
